import { useUnit } from 'effector-react';
import _ from 'lodash';
import $$api from 'models/api';
import $$auth from 'models/auth';
import { $global } from 'models/store-global';
import { useLocation } from 'react-router-dom';

import $$participantWidget from 'components/QuestionsEngine/Widgets/Participant/participant-widget-model';
import CommunityTab from 'components/StyledTemplates/CommunityTab';
import { Button, Frame } from 'components/StyledTemplates/StyledTemplates';
import {
    Check,
    CheckFrame,
    Divider,
    DocumentNameFrame,
    NotificationsPlate,
    NotificationsPlateHeader,
    NotifiedAndSharedFrame,
    RightColumnTopTabsWrapper,
    RoleFrame,
    StatusFrame,
    SupportHeading,
    Workspace,
} from 'components/pages/Dashboard/common/CommunityPageStyles';
import AddObserver from 'components/pages/Dashboard/participants/AddObserver';
import ParticipantInfoBlock from 'components/pages/Dashboard/participants/ParticipantInfoBlock';

import AlertsService from 'services/AlertsService';
import ModalsService from 'services/ModalsService';
import ParticipantsService from 'services/ParticipantsService';
import { phoneToWidgetFormat } from 'services/Utils';

import useDocumentTitle from 'hooks/useDocumentTitle';
import useResources from 'hooks/useResources';

const useParticipants = () => {
    const path = useLocation().pathname.split('/').slice(1);
    const selectedGroupMapping =
        {
            advocates: 'Advocate',
            beneficiaries: 'Beneficiary',
            observers: 'Observer',
        }[path[1]] ?? 'Not defined mapping';
    let participants = useUnit($global.map((state) => state.community?.realm || []));
    const docs = useUnit($global.map((state) => state.community?.docs || []));
    const tab = useUnit($global.map((state) => state.temp?.selected_community_participant || -1));
    const currentParticipant = useUnit($$auth.$currentParticipant);
    participants = participants
        ?.filter?.(
            (i: any) =>
                i?.roles
                    ?.filter?.((j: any) => j?.group_mapping === selectedGroupMapping)
                    ?.map?.(({ document_id }: any) => _.find(docs, { document_id })?.ownership)
                    ?.flat?.()
                    ?.includes?.(tab) ||
                (selectedGroupMapping === 'Observer' && ParticipantsService.isAdvisor(i)),
        )
        ?.map?.((i: any) => ({
            ...i,
            roles: (i?.roles ?? []).filter((j: any) => j?.group_mapping === selectedGroupMapping),
            isSpouse: !!i.roles?.find((r: any) => r.role_type_code === 'Spouse_or_Partner'),
        }));

    if (selectedGroupMapping === 'Observer') {
        participants = participants.filter((p: any) => p.participant_id !== currentParticipant?.participant_id);
    }
    return participants;
};

const ParticipantsPage = () => {
    const { t } = useResources();
    const path = useLocation().pathname.split(`/`).slice(1);

    const tab = useUnit($global.map((state) => state.temp?.selected_community_participant || -1));
    const isObserversTab = path[1] === 'observers';
    const documents = useUnit($global.map((state) => state.community?.docs || []));
    const participants = useParticipants();
    const authorizedUser = useUnit($$auth.$currentParticipant);
    const references = useUnit($$participantWidget.$participantReferences);

    useDocumentTitle(`${path[1]?.[0].toUpperCase?.() + path[1]?.slice(1)}`);

    return (
        <>
            {isObserversTab && <AddObserver documents={documents} tab={tab} observers={participants} />}
            <>
                {participants.map((participant, index) => {
                    const selectedParticipantAddressIsVisible = ParticipantsService.isAddressVisible(participant);
                    const participantIsOriginator = participant.participant_id === authorizedUser?.participant_id;
                    const participantsDocuments = (participant.roles || []).filter((role) => {
                        const originalDocument = documents.find(
                            (document) => document.document_id === role.document_id,
                        );
                        return (originalDocument?.ownership || []).includes(tab) && Boolean(role.notify);
                    });
                    const tabValue = $global
                        .map((state) => state.temp?.selected_community_participant || '')
                        .getState();
                    const targetValue = {
                        advocates: 'Advocate',
                        beneficiaries: 'Beneficiary',
                        observers: 'Observer',
                    }[path[1]];

                    const ableToResend =
                        participantsDocuments.filter(
                            (document) => document.notify === true || document.visibility_code === 'full_doc',
                        ).length > 0;

                    return (
                        <CommunityTab
                            key={`${tab}_${participant?.participant_id}`}
                            title={`${participant?.full_name}`}
                            closable={true}
                            isOpenedDefault={index === 0}
                            extraHeader="@media (max-width: 991px) { flex-direction: row; };"
                            // extra={`width: 900px;`}
                        >
                            <RightColumnTopTabsWrapper
                                visibleItemsNumber={
                                    +selectedParticipantAddressIsVisible +
                                    +Boolean(participant.email) +
                                    +Boolean(participant.phone_number)
                                }
                            >
                                <ParticipantInfoBlock participant={participant} />
                                <div className="col-md-4 d-flex flex-column align-items-end">
                                    <div className="d-flex flex-column w-100">
                                        {!participantIsOriginator && (
                                            <Button
                                                data-qaid="update_button"
                                                variant="secondary"
                                                extra="width: auto !important; flex: inherit !important;"
                                                className="mb-2"
                                                onClick={() => {
                                                    ModalsService.showModal('UPDATE_PARTICIPANT', {
                                                        ...participant,
                                                        isObserver: isObserversTab,
                                                        phone_number: participant.phone_number
                                                            ? phoneToWidgetFormat(participant.phone_number)
                                                            : undefined,
                                                        community: {
                                                            notify: participant?.roles?.[0]?.notify === true,
                                                            share:
                                                                participant?.roles
                                                                    ?.filter?.((i: any) =>
                                                                        [`full_doc`].includes(i?.visibility_code),
                                                                    )
                                                                    ?.map?.((i: any) => i.document_id) ?? [],
                                                            tab: tabValue,
                                                            target: targetValue,
                                                        },
                                                    });
                                                }}
                                            >
                                                {t(`communityPage.updateButtonLabel`)}
                                            </Button>
                                        )}
                                        {isObserversTab && (
                                            <Button
                                                data-qaid="delete_button"
                                                variant="danger"
                                                extra="width: auto !important; flex: inherit !important;"
                                                className="mb-2"
                                                onClick={() => {
                                                    ModalsService.showModality(
                                                        t('removeAdvisorFromCommunityModality.title'),
                                                        t('removeAdvisorFromCommunityModality.body'),
                                                        {
                                                            closeButton: t(
                                                                'removeAdvisorFromCommunityModality.closeButton',
                                                            ),
                                                            confirmButton: t(
                                                                'removeAdvisorFromCommunityModality.confirmButton',
                                                            ),
                                                            onConfirm: async () => {
                                                                await ParticipantsService.deleteObserver(
                                                                    participant.participant_id,
                                                                );
                                                            },
                                                        },
                                                    );
                                                }}
                                            >
                                                {t(`communityPage.deleteButtonLabel`)}
                                            </Button>
                                        )}
                                    </div>
                                    {ableToResend && (
                                        <Button
                                            // disabled={false}
                                            data-qaid="resend_notifications_button"
                                            variant="light"
                                            className="pl-0 pr-2 pl-sm-2"
                                            extra="width: max-content;"
                                            onClick={async () => {
                                                await $$api.participants.resendNotificationsFx({
                                                    id: participant?.participant_id,
                                                    params: {
                                                        tab: tabValue,
                                                        target: targetValue,
                                                    },
                                                });
                                                AlertsService.showSuccess(`Success`);
                                                ModalsService.hideModal(`UPDATE_PARTICIPANT`);
                                            }}
                                        >
                                            {t(`communityPagePopUp.resendNotifications`)}
                                        </Button>
                                    )}
                                </div>
                            </RightColumnTopTabsWrapper>
                            {(!isObserversTab || !ParticipantsService.isAdvisor(participant)) && (
                                <>
                                    <Divider extended={true} />
                                    <NotificationsPlateHeader>
                                        <SupportHeading>
                                            {t(`communityPage.documents`)} ({participantsDocuments.length})
                                        </SupportHeading>
                                        <SupportHeading>{t(`communityPage.documentStatus`)}</SupportHeading>
                                        <SupportHeading>{t(`communityPage.notify`)}</SupportHeading>
                                        <SupportHeading>{t(`communityPage.shareDocuments`)}</SupportHeading>
                                    </NotificationsPlateHeader>
                                    {participantsDocuments?.map?.((document: any, index: number) => {
                                        return (
                                            <NotificationsPlate key={index}>
                                                <Frame>
                                                    <DocumentNameFrame>{document.document_name}</DocumentNameFrame>
                                                    <RoleFrame>
                                                        <span>{t(`communityPage.role`)}</span>
                                                        <span>
                                                            {t(
                                                                `participantRole.${ParticipantsService.getRoleWithContext(
                                                                    participant.participant_id,
                                                                    document?.role_type_code,
                                                                    references,
                                                                )}`,
                                                            )}
                                                        </span>
                                                    </RoleFrame>
                                                </Frame>
                                                <StatusFrame>
                                                    {t(`document_status_code.${document?.document_status_code}`)}
                                                </StatusFrame>
                                                <CheckFrame>{document?.notify === true && <Check />}</CheckFrame>
                                                <CheckFrame>
                                                    {[`full_doc`].includes(document?.visibility_code) && <Check />}
                                                </CheckFrame>
                                                <NotifiedAndSharedFrame>
                                                    {document?.notify === true &&
                                                        [`full_doc`].includes(document?.visibility_code) &&
                                                        t('communityPage.notifiedAndShared')}
                                                    {document?.notify === true &&
                                                        ![`full_doc`].includes(document?.visibility_code) &&
                                                        t('communityPage.notified')}
                                                    {document?.notify === false &&
                                                        [`full_doc`].includes(document?.visibility_code) &&
                                                        t('communityPage.shared')}
                                                </NotifiedAndSharedFrame>
                                            </NotificationsPlate>
                                        );
                                    })}
                                </>
                            )}
                        </CommunityTab>
                    );
                })}
                {participants?.length === 0 && (
                    <Workspace extra="width: 100%;">
                        <div className="mb-2">{t(`communityPage.empty_${path[1]}_placeholder`)}</div>
                    </Workspace>
                )}
            </>
        </>
    );
};

export default ParticipantsPage;
